import { useMemo } from "preact/hooks"
import useSWR from "swr"
import { Loader2, X } from "lucide-react"
import { api_fetch } from "../../../../api/client"
import { currencyFormatter } from "../../../../utils"
import { listLedgerAccounts, LedgerAccountJson } from "../../../../api/accounting"
import { AutocompleteSelect, SelectItem } from "../../../../accounting/autocomplete_select"
import { Button } from "../../../../components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../../components/ui/dialog"

interface tactic_account_mapping {
  id: string
  tactic: string
  sub_dimension: string | null
  account_id: string
}

interface tactic_usage {
  tactic: string
  planned_total: number
}

interface tactic_map_list {
  mappings: tactic_account_mapping[]
  tactics: tactic_usage[]
}

interface TacticAccountMapModalProps {
  open: boolean
  onClose: () => void
}

/**
 * Maps promotion tactics (and promotion types) to GL ledger accounts so the month-end accrual
 * report can assign regular-promotion planned spend to accounts. Unmapped tactics surface as the
 * "(Unmapped)" row on the report.
 */
export function TacticAccountMapModal({ open, onClose }: TacticAccountMapModalProps) {
  const { data: map_result, isLoading, mutate } = useSWR(open ? "tactic-account-map" : null, () =>
    api_fetch<tactic_map_list>("/promotions/tactic-account-map")
  )
  const { data: accounts_result } = useSWR(open ? "ledger-accounts" : null, () => listLedgerAccounts())

  const map_data = map_result?.ok ? map_result.value.data : null
  const accounts: LedgerAccountJson[] = accounts_result?.ok ? accounts_result.value.data : []

  const account_items: SelectItem[] = useMemo(
    () => accounts.map((a) => ({ id: a.id, name: a.expense_account, expense_account: a.expense_account, account_number: a.account_number })),
    [accounts]
  )
  const account_by_id = useMemo(() => new Map(account_items.map((a) => [a.id, a])), [account_items])

  // Tactics that actually route plan dollars (server pre-sorts by $ descending), plus any
  // already-mapped tactic no longer present on promotions.
  const tactics: tactic_usage[] = useMemo(() => {
    if (!map_data) return []
    const seen = new Set(map_data.tactics.map((t) => t.tactic.toLowerCase()))
    const extras = map_data.mappings
      .filter((m) => !seen.has(m.tactic.toLowerCase()))
      .map((m) => ({ tactic: m.tactic, planned_total: 0 }))
    return [...map_data.tactics, ...extras]
  }, [map_data])

  // v1 maps tactics without a sub-dimension; the API supports sub_dimension for later.
  const mapping_for = (tactic: string) =>
    map_data?.mappings.find((m) => m.tactic.toLowerCase() === tactic.toLowerCase() && !m.sub_dimension)

  async function save_mapping(tactic: string, account: SelectItem) {
    await api_fetch("/promotions/tactic-account-map", {
      method: "POST",
      body: { tactic, sub_dimension: null, account_id: account.id },
    })
    mutate()
  }

  async function clear_mapping(mapping: tactic_account_mapping) {
    await api_fetch(`/promotions/tactic-account-map/${mapping.id}`, { method: "DELETE" })
    mutate()
  }

  return (
    <Dialog open={open} onOpenChange={(is_open: boolean) => !is_open && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        onInteractOutside={(e: Event) => {
          // Prevent closing when clicking on popover content (AutocompleteSelect uses Popover)
          const target = e.target as HTMLElement
          if (
            target.closest("[data-radix-popper-content-wrapper]") ||
            target.closest('[role="listbox"]') ||
            target.closest("[cmdk-list]") ||
            target.closest("[cmdk-input]") ||
            target.closest("[cmdk-item]")
          ) {
            e.preventDefault()
          }
        }}>
        <DialogHeader>
          <DialogTitle>Map Tactics to Ledger Accounts</DialogTitle>
          <DialogDescription>
            Promotion planned spend is assigned to GL accounts through these mappings. Unmapped tactics show up as
            "(Unmapped)" on the month-end report.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div class="flex items-center justify-center py-8 text-gray-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Loading…
          </div>
        )}

        {!isLoading && tactics.length === 0 && (
          <p class="text-sm text-gray-500 py-4">No promotion tactics found for this organization.</p>
        )}

        <div class="overflow-y-auto flex-1 space-y-2 pr-1">
          {tactics.map(({ tactic, planned_total }) => {
            const mapping = mapping_for(tactic)
            const selected = mapping ? account_by_id.get(mapping.account_id) : undefined
            return (
              <div key={tactic} class="flex items-center gap-3">
                <span
                  class={`h-2 w-2 rounded-full shrink-0 ${mapping ? "bg-green-500" : "bg-amber-500"}`}
                  title={mapping ? "Mapped" : "Not mapped"}
                />
                <div class="w-56 shrink-0 min-w-0">
                  <div class="text-sm truncate" title={tactic}>
                    {tactic}
                  </div>
                  <div class="text-xs text-gray-500">{currencyFormatter(planned_total)} planned</div>
                </div>
                <div class="flex-1 min-w-0">
                  <AutocompleteSelect
                    values={account_items}
                    initial={selected}
                    insideModal={true}
                    onSelect={(account: SelectItem) => save_mapping(tactic, account)}
                  />
                </div>
                {mapping && (
                  <Button variant="ghost" size="sm" onClick={() => clear_mapping(mapping)} title="Clear mapping">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
